import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button.tsx';
import { Input } from '@/components/ui/input.tsx';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.tsx';
import { useForgotPasswordMutation } from '@/features/auth/hooks/mutations/forgot-password.ts';
import { LoadingSpinner } from '@/components/ui/loading-spinner.tsx';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { StatusAlert } from '@/features/auth/components/status-alert.tsx';
import { PrimeLogo } from '@/assets/prime-logo.tsx';

const getForgotPasswordSchema = (translation?: {
  emailRequired: string;
  emailValid: string;
}) => {
  const messages = translation ?? {
    emailRequired: 'Email is required',
    emailValid: 'Please enter a valid email',
  };

  return z.object({
    email: z.email({
      error: (issue) =>
        issue.input === undefined ? messages.emailRequired : messages.emailValid,
    }),
  });
};

export const ForgotPassword = () => {
  const { t } = useTranslation();

  const forgotPasswordSchema = getForgotPasswordSchema({
    emailValid: t('auth.errors.emailValid'),
    emailRequired: t('auth.errors.emailRequired'),
  });

  const form = useForm({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const {
    mutate: forgotPasswordMutation,
    isPending,
    isSuccess,
    error,
  } = useForgotPasswordMutation({
    onSuccess: () => {
      form.reset();
    },
  });

  async function onSubmit(values: z.infer<typeof forgotPasswordSchema>) {
    forgotPasswordMutation({ email: values.email });
  }

  return (
    <div>
      <div className="flex flex-col items-center justify-center gap-2">
        <PrimeLogo width="200" height="58" />
      </div>
      <div className="mt-8 flex max-w-[400px] flex-col gap-4">
        {isSuccess && (
          <StatusAlert type="success" message={t('auth.forgotPassword.successMessage')} />
        )}
        {!!error?.message && <StatusAlert type="error" message={error?.message} />}
        <h2 className="text-xl font-bold">{t('auth.forgotPassword.title')}</h2>
        <span className="block">{t('auth.forgotPassword.description')}</span>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="email" className="text-[16px]">
                      {t('auth.forgotPassword.emailLabel')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        id="email"
                        type="email"
                        autoComplete="emailAddress"
                        placeholder={t('auth.forgotPassword.emailPlaceholder')}
                        className="text-[16px]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <Button
              disabled={isPending}
              type="submit"
              className="flex w-full items-center gap-2"
              size="lg"
            >
              {isPending && <LoadingSpinner size={24} />}
              <span className="text-[16px] select-none">
                {t('auth.forgotPassword.requestButton')}
              </span>
            </Button>
          </form>
        </Form>
        <div className="flex justify-center gap-1 text-center">
          <Link to="/" className="link-underline">
            {t('auth.common.returnToLogin')}
          </Link>
        </div>
      </div>
    </div>
  );
};
